import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { MEGA_MENU } from "../content/site";

const NAV_LINKS = [
  { label: "Services", href: "/services", mega: true },
  { label: "Solutions", href: "/solutions" },
  { label: "Industries", href: "/industries" },
  { label: "Data & AI", href: "/data-ai" },
  { label: "Company", href: "/company" },
  { label: "Insights", href: "/insights" },
  { label: "Contact", href: "/contact" },
  { label: "Careers", href: "/careers", careers: true },
];

const MegaMenu = ({ open }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="absolute left-1/2 top-full z-50 w-[min(96vw,1100px)] -translate-x-1/2 pt-4"
        data-testid="services-mega-menu"
      >
        <div className="grid grid-cols-2 gap-x-10 gap-y-8 rounded-2xl border border-tark-line bg-tark-surface/95 p-9 shadow-2xl shadow-black/60 backdrop-blur-xl lg:grid-cols-4">
          {MEGA_MENU.map((cat) => (
            <div key={cat.category}>
              <Link
                to={cat.href}
                data-testid={`mega-cat-${cat.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`group flex items-center gap-1.5 font-display text-sm font-bold tracking-wide ${cat.highlight ? "text-tark-cyan" : "text-tark-ink"} hover:text-tark-cyan`}
              >
                {cat.category}
                <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
              <div className="mt-3 h-px w-10 bg-gradient-to-r from-tark-blue to-transparent" />
              <ul className="mt-3 space-y-1.5">
                {cat.items.map((item) => (
                  <li key={item}>
                    <Link to={cat.href} className="text-[13px] text-tark-mut transition-colors hover:text-tark-ink">
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Navbar = () => {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const openMega = () => { clearTimeout(closeTimer.current); setMegaOpen(true); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setMegaOpen(false), 180); };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-tark-line/70 bg-tark-bg/85 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="TARK home"><Logo /></Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((link) =>
            link.mega ? (
              <div key={link.label} onMouseEnter={openMega} onMouseLeave={scheduleClose} className="relative">
                <button
                  data-testid="nav-services-trigger"
                  className={`flex items-center gap-1 rounded-full px-4 py-2 font-display text-sm font-semibold transition-colors ${megaOpen ? "text-tark-cyan" : "text-tark-mut hover:text-tark-ink"}`}
                  aria-expanded={megaOpen}
                >
                  {link.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
                </button>
                <div onMouseEnter={openMega} onMouseLeave={scheduleClose}>
                  <MegaMenu open={megaOpen} />
                </div>
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-4 py-2 font-display text-sm font-semibold transition-colors ${
                  link.careers
                    ? "text-tark-cyan hover:bg-tark-cyan/10"
                    : location.pathname === link.href ? "text-tark-ink" : "text-tark-mut hover:text-tark-ink"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/start-a-project"
            data-testid="nav-talk-to-expert"
            className="group inline-flex items-center gap-2 rounded-full bg-tark-blue px-5 py-2.5 font-display text-sm font-bold text-white transition-colors hover:bg-tark-vivid"
          >
            Talk to an Expert
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          className="rounded-lg border border-tark-line p-2 text-tark-ink lg:hidden"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="max-h-[calc(100vh-76px)] overflow-y-auto border-t border-tark-line bg-tark-bg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-6 py-6">
              <button
                className="flex w-full items-center justify-between rounded-lg px-3 py-3 font-display text-base font-semibold text-tark-ink"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                data-testid="mobile-services-toggle"
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileServicesOpen && (
                <div className="space-y-1 pb-2 pl-4">
                  {MEGA_MENU.map((cat) => (
                    <Link key={cat.category} to={cat.href} className="block rounded-lg px-3 py-2 text-sm text-tark-mut hover:text-tark-cyan">
                      {cat.category}
                    </Link>
                  ))}
                </div>
              )}
              {NAV_LINKS.filter((l) => !l.mega).map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`block rounded-lg px-3 py-3 font-display text-base font-semibold ${link.careers ? "text-tark-cyan" : "text-tark-ink"}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4">
                <Link
                  to="/start-a-project"
                  data-testid="mobile-nav-talk-to-expert"
                  className="flex items-center justify-center gap-2 rounded-full bg-tark-blue px-5 py-3.5 font-display text-sm font-bold text-white"
                >
                  Talk to an Expert <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
